import { useFormatting } from "@msys/ui";
import type {
  GridColDef,
  GridRenderCellParams,
  GridTreeNodeWithRender,
} from "@mui/x-data-grid-premium";
import React from "react";
import { itemHasEstimatedCalculationBox } from "../../doc-items/app-conditions/boxes.js";
import type { EstimatedCalculation__ItemFragment } from "../../doc-items/boxes/EstimatedCalculationBox.generated.js";
import type { DocDataGrid_ItemFragment } from "../DocDataGrid.generated.js";
import {
  DataGridFloatWithExpressionDisplay,
  DataGridFloatWithExpressionEditCell,
} from "../cells/DataGridFloatWithExpressionEditCell.js";
import { makeCalculationValueSetter } from "../cells/helpers.js";
import type { ModifyItemCalculationFn } from "../quote/useModifyItemCalculation.js";
import type { DocDataGridRow } from "../types.js";

interface Options<
  T extends DocDataGrid_ItemFragment & EstimatedCalculation__ItemFragment,
> {
  headerName: string;
  projectId: string | null;
  docId: string;
  canEdit: boolean;
  modifyItemCalculation: ModifyItemCalculationFn;
  itemCanBeEdited?: (item: T) => boolean;
}

function QuantityDisplayCell<
  T extends DocDataGrid_ItemFragment & EstimatedCalculation__ItemFragment,
>({
  params,
  projectId,
  docId,
}: {
  params: GridRenderCellParams<
    DocDataGridRow<T>,
    any,
    any,
    GridTreeNodeWithRender
  >;
  projectId: string | null;
  docId: string;
}) {
  const { getFormattedFloat } = useFormatting();
  const formattedValue = getFormattedFloat(params.value ?? 0);
  return (
    <DataGridFloatWithExpressionDisplay<T>
      params={params}
      fieldName="estimatedQuantity"
      projectId={projectId}
      docId={docId}
      formattedValue={formattedValue}
    />
  );
}

export function createEstimatedQuantityColumn<
  T extends DocDataGrid_ItemFragment & EstimatedCalculation__ItemFragment,
>(options: Options<T>): GridColDef<DocDataGridRow<T>> {
  return {
    field: "estimatedQuantity",
    headerName: options.headerName,
    width: 80,
    editable: options.canEdit,
    sortable: false,
    filterable: false,
    valueGetter: (_value, row) => row.item?.proposedCalculation?.quantity ?? 0,
    // estimatedQuantity (mutation) maps to quantity (proposedCalculation)
    valueSetter: makeCalculationValueSetter<T>()("quantity", (itemId, value) =>
      options.modifyItemCalculation(itemId, {
        estimatedQuantity: value,
      })
    ),
    renderCell: params => {
      if (params.row.kind === "input" || !params.row.item) return null;
      const item = params.row.item;
      if (item.isRootItem) return null;
      if (!itemHasEstimatedCalculationBox(item)) return null;
      if (options.itemCanBeEdited?.(item) === false) return null;
      return (
        <QuantityDisplayCell
          params={params}
          projectId={options.projectId}
          docId={options.docId}
        />
      );
    },
    renderEditCell: params => {
      if (params.row.kind === "input" || !params.row.item) return null;
      const item = params.row.item;
      if (item.isRootItem) return null;
      if (!itemHasEstimatedCalculationBox(item)) return null;
      if (options.itemCanBeEdited?.(item) === false) return null;
      // Don't allow edit when item has expression
      const expression = item.attributeExpressions?.find(
        e => e.attribute === "estimatedQuantity"
      );
      if (expression) return null;
      return <DataGridFloatWithExpressionEditCell {...params} min={0} />;
    },
  };
}
